import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import SyfFaqs from './faqs';
import TioStepOverview from 'tio-common/components/tio/step-overview';
import type MatchPlanModel from 'tio-common/models/match-plan';
import { Button } from 'tio-ui/components/buttons';
import { Section, VStack } from 'tio-ui/components/layout';

export interface SyfEnrollmentSignature {
  Args: {
    onBeginEnrollment: () => void;
    beginEnrollmentLoading: boolean;
    matchPlan: MatchPlanModel;
  };
}

class SyfEnrollmentComponent extends Component<SyfEnrollmentSignature> {
  @tracked options: Record<string, boolean> = trackedObject({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  option1Text = `<div><span class="text-fuscia-300">I do not contribute</span> to my employer's 401(k)</div>`;
  option2Text = `<div>I make <span class="text-fuscia-300">contributions</span> to my 401(k) and do not receive the full match from my employer</div>`;
  option3Text = `<div>I make contributions to my 401(k), but <span class="text-fuscia-300">I am unsure whether I receive the full match from my employer</span></div>`;
  option4Text = `<div><span class="text-fuscia-300">I contribute to my 401(k)</span> and receive the full possible match from my employer</div>`;

  get secureYourFutureHeader() {
    return `What is <span class="text-violet-800">Secure Your Future</span>?`;
  }

  get secureOptionCards() {
    return [
      {
        text: this.option1Text,
        dataKey: 'option1',
      },
      {
        text: this.option2Text,
        dataKey: 'option2',
      },
      {
        text: this.option3Text,
        dataKey: 'option3',
      },
      {
        text: this.option4Text,
        dataKey: 'option4',
      },
    ];
  }

  get enrollmentSteps() {
    return ['Connect your loans', 'Confirm your loan payments', 'Receive matching contributions'];
  }

  showOptionNumber = (option: string) => {
    for (const key in this.options) {
      if (key !== option) {
        this.options[key] = false;
      }
    }
    this.options[option] = true;

    document.getElementById('button_container')?.scrollIntoView({ behavior: 'smooth' });
  };

  <template>
    <h1 class="text-4xl md:text-5xl font-semibold text-center py-6">{{t
        "syf.enrollment.heading"
      }}</h1>
    <h2 class="text-2xl text-center mx-auto font-light">{{t "syf.enrollment.subheading"}}</h2>

    <Section class="bg-violet-50!">
      <:header>
        {{htmlSafe this.secureYourFutureHeader}}
      </:header>
      <:body>
        <VStack>
          <p>
            {{t "syf.enrollment.secure_2_intro"}}
            <span class="font-semibold">{{t "syf.enrollment.start_date"}}</span>
          </p>
          <p>
            <span class="text-violet-800 font-semibold">{{t "syf.enrollment.program_name"}}</span>
            {{t "syf.enrollment.benefit_description"}}
            <span class="font-semibold">{{t "syf.enrollment.qualifying_payments"}}</span>
            {{t "syf.enrollment.to_get_an"}}
            <span class="font-semibold">{{t "syf.enrollment.employer_match"}}</span>
          </p>
        </VStack>
      </:body>
    </Section>

    <Section class="bg-violet-50!">
      <:header>{{t "syf.enrollment.ready_to_enroll"}}</:header>
      <:body>
        <VStack>
          <p>
            {{t "syf.enrollment.enrollment_process"}}
          </p>
          <div class="flex justify-center">
            <TioStepOverview @steps={{this.enrollmentSteps}} />
          </div>

          <Button
            @intent="primary"
            {{on "click" @onBeginEnrollment}}
            @isRunning={{@beginEnrollmentLoading}}
            class="w-60 mx-auto"
          >
            {{t "syf.enrollment.start"}}
          </Button>
        </VStack>
      </:body>
    </Section>

    <Section>
      <:header>{{t "syf.enrollment.want_to_learn_more"}}</:header>
      <:body>
        <VStack>
          <p class="leading-relaxed">
            {{t "syf.enrollment.learn_more_intro"}}
          </p>
          <p>{{t "syf.enrollment.choose_option"}}
            <span class="font-semibold">{{t "syf.enrollment.choose_option_bold"}}</span></p>

          <div id="button_container" class="flex flex-wrap justify-center gap-4 font-semibold">
            {{#each this.secureOptionCards as |card|}}
              <button
                type="button"
                class="border border-gray-100 rounded-md shadow overflow-hidden w-64 h-28 p-2 flex items-center hover:bg-violet-100"
                {{on "click" (fn this.showOptionNumber card.dataKey)}}
              >
                <div class="text-center">
                  {{htmlSafe card.text}}
                </div>
              </button>
            {{/each}}
          </div>
        </VStack>
      </:body>
    </Section>
    {{#if this.options.option1}}
      <Section class="bg-violet-50!">
        <:header>{{htmlSafe this.option1Text}}</:header>
        <:subHeader>{{t "syf.enrollment.how_can_this_help"}}</:subHeader>
        <:body>
          <VStack>
            <h1 class="font-medium">{{t "syf.enrollment.option1.intro"}}</h1>
            <ol class="list-decimal p-4 leading-loose">
              <li>{{t "syf.enrollment.option1.point1"}}</li>
              <li>{{t "syf.enrollment.option1.point2"}}</li>
            </ol>
          </VStack>
        </:body>
      </Section>
    {{/if}}

    {{#if this.options.option2}}
      <Section class="bg-violet-50!">
        <:header>{{htmlSafe this.option2Text}}</:header>
        <:subHeader>{{t "syf.enrollment.how_can_this_help"}}</:subHeader>
        <:body>
          <VStack>
            <h1 class="font-medium">{{t "syf.enrollment.option2.intro"}}</h1>
            <ol class="list-decimal p-4 leading-loose">
              <li>{{t "syf.enrollment.option2.point1"}}</li>
              <li>{{t "syf.enrollment.option2.point2"}}</li>
            </ol>
          </VStack>
        </:body>
      </Section>
    {{/if}}

    {{#if this.options.option3}}
      <Section class="bg-violet-50!">
        <:header>{{htmlSafe this.option3Text}}</:header>
        <:subHeader>{{t "syf.enrollment.how_can_this_help"}}</:subHeader>
        <:body>
          <VStack>
            <h1 class="font-medium">{{t "syf.enrollment.option3.intro"}}</h1>
            <ol class="list-decimal p-4 leading-loose">
              <li>{{t "syf.enrollment.option3.point1"}}</li>
              <li>{{t "syf.enrollment.option3.point2"}}</li>
              <li>{{t "syf.enrollment.option3.point3"}}</li>
            </ol>
          </VStack>
        </:body>
      </Section>
    {{/if}}

    {{#if this.options.option4}}
      <Section class="bg-violet-50!">
        <:header>{{htmlSafe this.option4Text}}</:header>
        <:subHeader>{{t "syf.enrollment.how_can_this_help"}}</:subHeader>
        <:body>
          <VStack>
            <h1 class="font-medium">{{t "syf.enrollment.option4.intro"}}</h1>
            <ol class="list-decimal p-4 leading-loose">
              <li>{{t "syf.enrollment.option4.point1"}}</li>
              <li>{{t "syf.enrollment.option4.point2"}}</li>
              <li>{{t "syf.enrollment.option4.point3"}}
                <LinkTo
                  @route="authenticated.repaying-student-debt.coaching.schedule"
                  class="font-semibold text-ocean-600 hover:underline"
                >{{t "syf.enrollment.option4.here"}}</LinkTo>
                {{t "syf.enrollment.option4.to_contact_coach"}}</li>
            </ol>
          </VStack>
        </:body>
      </Section>
    {{/if}}

    <SyfFaqs @faqs={{@matchPlan.faqs}} />
  </template>
}

export default SyfEnrollmentComponent;
